// redux/ugc/interactionSlice.ts

import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import { interactionApi } from "../../services/ugc/interactionApi";
import type {
  ReactionSummaryDTO,
  ReactionTogglePayload,
} from "../../types/ugc/interactionTypes";

type Status = "idle" | "loading" | "succeeded" | "failed";

export const interactionKeyFor = (content_type: string, object_id: number) =>
  `${content_type}:${object_id}`;

interface InteractionState {
  summaryByKey: Record<string, ReactionSummaryDTO | undefined>;
  statusByKey: Record<string, Status | undefined>;
  errorByKey: Record<string, string | undefined>;
}

const initialState: InteractionState = {
  summaryByKey: {},
  statusByKey: {},
  errorByKey: {},
};

export const fetchReactionSummary = createAsyncThunk<
  { key: string; summary: ReactionSummaryDTO },
  { content_type: string; object_id: number }
>("interactions/fetchReactionSummary", async ({ content_type, object_id }) => {
  const summary = await interactionApi.getReactionSummary(content_type, object_id);
  return {
    key: interactionKeyFor(content_type, object_id),
    summary,
  };
});

export const toggleReaction = createAsyncThunk<
  { key: string; summary: ReactionSummaryDTO },
  ReactionTogglePayload
>("interactions/toggleReaction", async (payload) => {
  const summary = await interactionApi.toggleReaction(payload);
  return {
    key: interactionKeyFor(payload.content_type, payload.object_id),
    summary,
  };
});

const interactionSlice = createSlice({
  name: "interactions",
  initialState,
  reducers: {
    applyRealtimeReactionSummary(
      state,
      action: PayloadAction<{ key: string; summary: ReactionSummaryDTO }>
    ) {
      state.summaryByKey[action.payload.key] = action.payload.summary;
      state.statusByKey[action.payload.key] = "succeeded";
    },
    clearInteractionError(
      state,
      action: PayloadAction<{ key: string }>
    ) {
      delete state.errorByKey[action.payload.key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReactionSummary.pending, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        state.statusByKey[interactionKeyFor(content_type, object_id)] = "loading";
      })
      .addCase(fetchReactionSummary.fulfilled, (state, action) => {
        state.summaryByKey[action.payload.key] = action.payload.summary;
        state.statusByKey[action.payload.key] = "succeeded";
      })
      .addCase(fetchReactionSummary.rejected, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        const key = interactionKeyFor(content_type, object_id);
        state.statusByKey[key] = "failed";
        state.errorByKey[key] = action.error.message;
      })

      .addCase(toggleReaction.pending, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        state.statusByKey[interactionKeyFor(content_type, object_id)] = "loading";
      })
      .addCase(toggleReaction.fulfilled, (state, action) => {
        state.summaryByKey[action.payload.key] = action.payload.summary;
        state.statusByKey[action.payload.key] = "succeeded";
      })
      .addCase(toggleReaction.rejected, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        const key = interactionKeyFor(content_type, object_id);
        state.statusByKey[key] = "failed";
        state.errorByKey[key] = action.error.message;
      });
  },
});

export const {
  applyRealtimeReactionSummary,
  clearInteractionError,
} = interactionSlice.actions;

export default interactionSlice.reducer;